import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { Close as CloseIcon, GetApp as InstallIcon } from '@mui/icons-material';

/**
 * InstallPrompt Component
 * 
 * Displays a custom install prompt for PWA installation.
 * Shows when the browser's beforeinstallprompt event fires.
 */
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      // Check if running as standalone (installed PWA)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      // Check localStorage flag
      if (localStorage.getItem('pwa-installed') === 'true') {
        setIsInstalled(true);
        return true;
      }
      
      return false;
    };

    if (checkIfInstalled()) {
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the default browser install prompt
      e.preventDefault();
      
      // Store the event for later use
      setDeferredPrompt(e);
      
      // Check if user has previously dismissed the prompt
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      // Show prompt if not dismissed or dismissed more than 7 days ago
      if (!dismissed || daysSinceDismissed > 7) {
        // Small delay to ensure UI is ready
        setTimeout(() => {
          setShowPrompt(true);
        }, 1000);
      }
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Also check periodically if the event hasn't fired yet (for testing)
    // This helps in development when service worker might not be ready immediately
    let checkInterval = null;
    if (process.env.NODE_ENV === 'development') {
      checkInterval = setInterval(() => {
        // Only check if we haven't received the event yet
        if (!deferredPrompt && !showPrompt && !isInstalled) {
          // In development, we can manually trigger for testing
          // But only if service worker is registered
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(() => {
              // Service worker is ready, but event might not fire in dev
              // This is expected - beforeinstallprompt requires HTTPS in production
            });
          }
        }
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [deferredPrompt, showPrompt, isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.warn('Install prompt not available. Make sure you are on HTTPS and the app meets PWA requirements.');
      return;
    }

    try {
      // Show the browser's install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);

      // Track installation
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
        setIsInstalled(true);
        console.log('PWA installation accepted');
      } else {
        console.log('PWA installation declined');
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed
  if (isInstalled || !showPrompt) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        left: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 'auto' },
        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
        p: 2.5,
        zIndex: 9999,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        animation: 'slideUp 0.3s ease-out',
        '@keyframes slideUp': {
          from: {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          to: {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <InstallIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Install SLG Education
            </Typography>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ ml: 1, mt: -0.5 }}
              aria-label="Close install prompt"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Install our app for quick access, offline support, and a better experience!
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleInstallClick}
              startIcon={<InstallIcon />}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                py: 1,
              }}
            >
              Install App
            </Button>
            <Button
              variant="outlined"
              onClick={handleDismiss}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 2,
              }}
            >
              Later
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default InstallPrompt;

